import { useState } from "react";
import { apiRequest } from "../../network/apiClient";
import { API_ENDPOINTS } from "../../api/apiEndpoints";
import { toast } from "react-toastify";
import { countryCodes, language_options } from '../../components/Constants.jsx';

// ── Types ────────────────────────────────────────────────────────────────────

interface CountryCode {
    label: string;
    value: string;
}

interface NewContact {
    salutation: string;
    name: string;
    designation: string;
    email: string;
    mobile_country_code: string;
    mobile_no: string;
    office_no: string;
    reporting_to: string;
    race_of_pic: string;
    race_of_pic_others: string;
    language: string[];
    lead_id?: string | number;
}

interface AddContactsProps {
    showModal: boolean;
    setShowModal: (value: boolean) => void;
    leadID: string | number;
    onContactAdded: () => void;
}

// ── Constants ────────────────────────────────────────────────────────────────

const DEFAULT_CONTACT: NewContact = {
    salutation: "MR.",
    name: "",
    designation: "",
    email: "",
    mobile_country_code: "+60",
    mobile_no: "",
    office_no: "",
    reporting_to: "No One",
    race_of_pic: "Malay",
    race_of_pic_others: "",
    language: ["English"],
};

// ── Component ────────────────────────────────────────────────────────────────

const AddContacts = ({ showModal, setShowModal, leadID, onContactAdded }: AddContactsProps) => {
    const [showLanguageDropdown, setShowLanguageDropdown] = useState<boolean>(false);
    const [newContact, setNewContact] = useState<NewContact>(DEFAULT_CONTACT);
    const [emailError, setEmailError] = useState<string>("");

    // ── Handlers ──────────────────────────────────────────────────────────────

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
        const { name, value } = e.target;

        // Email validation — must contain '@'
        if (name === "email") {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            setEmailError(value && !emailRegex.test(value) ? "Please enter a valid email address" : "");
        }

        // Sanitize mobile number — strip whitespace and dashes, allow digits only
        const sanitizedValue = name === "mobile_no"
            ? value.replace(/[\s-]/g, "").replace(/\D/g, "")
            : value;

        setNewContact((prev) => ({ ...prev, [name]: sanitizedValue }));
    };

    const handleLanguageToggle = (lang: string, checked: boolean) => {
        setNewContact((prev) => ({
            ...prev,
            language: checked
                ? [...prev.language, lang]
                : prev.language.filter((l) => l !== lang),
        }));
    };

    const handleSubmit = async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();

        if (!newContact.name.trim()) {
            toast.error("Name is required");
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!newContact.email.trim()) {
            toast.error("Email is required");
            return;
        }

        if (!emailRegex.test(newContact.email)) {
            toast.error("Please enter a valid email address");
            setEmailError("Please enter a valid email address");
            return;
        }

        if (!newContact.mobile_no.trim()) {
            toast.error("Mobile number is required");
            return;
        }

        if (!/^\d+$/.test(newContact.mobile_no)) {
            toast.error("Mobile number must contain digits only");
            return;
        }

        if (newContact.mobile_no.length < 9) {
            toast.error("Mobile number must be at least 9 digits");
            return;
        }

        try {
            const payload = Object.fromEntries(
                Object.entries({
                    ...newContact,
                    lead_id: leadID,
                    mobile_no: `${newContact.mobile_country_code}${newContact.mobile_no}`,
                }).map(([key, value]) => [
                    key,
                    typeof value === "string" && value.trim() === "" ? null : value,
                ])
            );
            const result = await apiRequest(API_ENDPOINTS.UPDATE_ACCOUNTS.ADD_CONTACTS, "POST", payload);

            if (result) {
                onContactAdded();
            } else {
                console.error("Error adding contact");
            }

            toast.success("Contact successfully added");
        } catch (error) {
            toast.error("Unable to save contact");
        } finally {
            setNewContact(DEFAULT_CONTACT);
            setEmailError("");
            setShowModal(false);
            setShowLanguageDropdown(false);
        }
    };

    // ── Render ────────────────────────────────────────────────────────────────

    if (!showModal) return null;

    return (
        <div className="fixed inset-0 flex items-center justify-center z-50">
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black opacity-50"
                onClick={() => setShowModal(false)}
            />

            {/* Modal */}
            <div className="w-[65%] bg-white rounded-lg shadow-lg p-6 z-10">
                {/* Header */}
                <div className="w-full flex justify-center items-center mb-4 border-b">
                    <h2 className="text-xl font-bold mb-4">New Contact</h2>
                </div>

                {/* Form Grid */}
                <div className="grid grid-cols-2 gap-x-5 ml-3 text-[#475569]">

                    {/* Salutation */}
                    <div className="flex items-start mt-5">
                        <p className="border-b border-gray-200 pb-2 w-full flex">
                            <span className="w-1/2">Salutation</span>
                            <select
                                className="w-full bg-transparent focus:outline-none"
                                name="salutation"
                                value={newContact.salutation}
                                onChange={handleInputChange}
                                required
                            >
                                <option value="MR.">MR.</option>
                                <option value="MS.">MS.</option>
                                <option value="MRS.">MRS.</option>
                            </select>
                        </p>
                    </div>

                    {/* Name */}
                    <div className="flex items-start mt-5">
                        <p className="border-b border-gray-200 pb-2 w-full flex">
                            <span className="w-1/2">Name</span>
                            <input
                                type="text"
                                className="w-full"
                                name="name"
                                value={newContact.name}
                                onChange={handleInputChange}
                                required
                            />
                        </p>
                    </div>

                    {/* Designation */}
                    <div className="flex items-start mt-5">
                        <p className="border-b border-gray-200 pb-2 w-full flex">
                            <span className="w-1/2">Designation</span>
                            <input
                                type="text"
                                className="w-full"
                                name="designation"
                                value={newContact.designation}
                                onChange={handleInputChange}
                                required
                            />
                        </p>
                    </div>

                    {/* Email — with validation */}
                    <div className="flex items-start mt-5">
                        <div className="border-b border-gray-200 pb-2 w-full flex flex-col">
                            <div className="flex w-full">
                                <span className="w-1/2">Email</span>
                                <input
                                    type="text"
                                    className="w-full"
                                    name="email"
                                    value={newContact.email}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>
                            {emailError && (
                                <p className="text-red-500 text-xs mt-1 text-right">{emailError}</p>
                            )}
                        </div>
                    </div>

                    {/* Mobile Number — with country code */}
                    <div className="flex items-start mt-5">
                        <div className="border-b border-gray-200 pb-2 w-full flex">
                            <span className="w-1/3 whitespace-nowrap">Mobile Number</span>
                            <div className="flex flex-1 gap-2">
                                <select
                                    name="mobile_country_code"
                                    value={newContact.mobile_country_code}
                                    onChange={handleInputChange}
                                    className="bg-transparent focus:outline-none text-md"
                                >
                                    {countryCodes.map((code) => (
                                        <option key={code.value} value={code.value}>
                                            {code.value}
                                        </option>
                                    ))}
                                </select>
                                <input
                                    type="tel"
                                    className="flex-1 min-w-0"
                                    name="mobile_no"
                                    value={newContact.mobile_no}
                                    onChange={handleInputChange}
                                    placeholder="e.g. 123456789"
                                    required
                                />
                            </div>
                        </div>
                    </div>

                    {/* Office Number */}
                    <div className="flex items-start mt-5">
                        <p className="border-b border-gray-200 pb-2 w-full flex">
                            <span className="w-1/2 whitespace-nowrap">Office Number</span>
                            <input
                                type="text"
                                className="w-full"
                                name="office_no"
                                value={newContact.office_no}
                                onChange={handleInputChange}
                                required
                            />
                        </p>
                    </div>

                    {/* Reporting To */}
                    <div className="flex items-start mt-5">
                        <p className="border-b border-gray-200 pb-2 w-full flex">
                            <span className="w-1/2">Reporting to</span>
                            <select
                                className="w-full"
                                name="reporting_to"
                                value={newContact.reporting_to}
                                onChange={handleInputChange}
                                required
                            >
                                <option value="No One">No One</option>
                                <option value="Owner / CEO / Director">Owner / CEO / Director</option>
                                <option value="CFO / Finance Director / Finance Manager">CFO / Finance Director / Finance Manager</option>
                                <option value="Partner">Partner</option>
                            </select>
                        </p>
                    </div>

                    {/* Preferred Language */}
                    <div className="flex items-start mt-5">
                        <div className="border-b border-gray-200 pb-2 w-full flex">
                            <span className="w-1/2">Preferred Language</span>
                            <div className="w-full relative">
                                <div
                                    className="w-full border border-gray-300 rounded px-2 py-1 cursor-pointer bg-white flex justify-between items-center"
                                    onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
                                >
                                    <span>
                                        {newContact.language.length > 0
                                            ? newContact.language.join(", ")
                                            : "Select Language"}
                                    </span>
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="size-6">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                                    </svg>
                                </div>
                                {showLanguageDropdown && (
                                    <div className="absolute top-full left-0 w-full bg-white border border-gray-300 rounded mt-1 z-10 shadow-lg">
                                        {language_options.map((lang) => (
                                            <label key={lang} className="flex items-center px-3 py-2 hover:bg-gray-100 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    value={lang}
                                                    checked={newContact.language.includes(lang)}
                                                    onChange={(e) => handleLanguageToggle(lang, e.target.checked)}
                                                    className="mr-2"
                                                />
                                                {lang}
                                            </label>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Race */}
                    <div className="flex items-start mt-5">
                        <p className="border-b border-gray-200 pb-2 w-full flex">
                            <span className="w-1/2">Race</span>
                            <select
                                className="w-full"
                                name="race_of_pic"
                                value={newContact.race_of_pic}
                                onChange={handleInputChange}
                                required
                            >
                                <option value="Malay">Malay</option>
                                <option value="Chinese">Chinese</option>
                                <option value="Indian">Indian</option>
                                <option value="Others">Others</option>
                            </select>
                        </p>
                    </div>

                    {/* Race — Others specify */}
                    {newContact.race_of_pic === "Others" && (
                        <div className="flex items-start mt-5">
                            <p className="border-b border-gray-200 pb-2 w-full flex">
                                <span className="w-1/2 whitespace-nowrap">If Others, Specify</span>
                                <input
                                    type="text"
                                    className="w-full"
                                    name="race_of_pic_others"
                                    value={newContact.race_of_pic_others}
                                    placeholder="Please specify"
                                    onChange={handleInputChange}
                                    required
                                />
                            </p>
                        </div>
                    )}
                </div>

                {/* Footer Buttons */}
                <div className="flex justify-end mt-6 gap-4">
                    <button
                        onClick={handleSubmit}
                        className="mt-4 bg-blue-500 text-white px-4 py-2 rounded"
                    >
                        Save
                    </button>
                    <button
                        onClick={() => setShowModal(false)}
                        type="button"
                        style={{ backgroundColor: "#F9FAFB" }}
                        className="mt-4 border bg-white text-[#475569] px-4 py-2 rounded"
                    >
                        Dismiss
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AddContacts;